import { Text } from "atomize"
import { createContext, useContext, useLayoutEffect, useRef, useState } from "react"
import { theme } from "../settings/config"
import { isSmartphone } from "../atoms/SmartphoneView"
import { PHONE_HEADER_HEIGHT, PHONE_HEADER_TOP, PHONE_ISLAND_SIDE } from "../atoms/phoneBars"

// ──────────────────────────────────────────────────────────────────────────
// IL TITOLO DELLA PAGINA, ACCANTO ALLA PILLOLA
//
// Fuori dalle lezioni, sul telefono, in cima c'è una pillola corta in alto a
// destra (vedi PhoneHeader): il logo, due numeri della pagina, il tondo del
// circolo. Il titolo della pagina sta sulla sua stessa riga, a sinistra.
//
// Le due cose non si devono toccare mai. La pillola dice quanto è larga
// (allungata: quando si ritira il titolo è già scorso via, e se il posto
// seguisse la pillola il titolo ballerebbe), e il titolo si tiene libero
// quel posto. Se non ci sta, si rimpicciolisce fino a un minimo, e oltre il
// minimo finisce coi puntini: andare a capo spingerebbe giù la pagina.
//
// I numeri nella pillola li sa la pagina, non la fascia: ognuna li passa
// con usePhoneSummary.
// ──────────────────────────────────────────────────────────────────────────

const phoneTitleContext = createContext({
	summary: null,
	setSummary: () => {},
	pillWidth: 0,
	setPillWidth: () => {},
})

export function PhoneTitleProvider({ children }) {
	const [summary, setSummary] = useState(null)
	const [pillWidth, setPillWidth] = useState(0)
	return (
		<phoneTitleContext.Provider value={{ summary, setSummary, pillWidth, setPillWidth }}>
			{children}
		</phoneTitleContext.Provider>
	)
}

export const usePhoneTitle = () => useContext(phoneTitleContext)

// La riga sotto il logo, finché la pagina c'è: null se non c'è niente da
// dire. Mentre i numeri nuovi arrivano (un altro circolo) la pagina passa
// undefined, e resta la riga di prima: sparire e ricomparire farebbe
// ballare la pillola, e il titolo con lei.
//
// Cambiando pagina la riga della pagina di prima se ne va, ma il suo posto
// resta (PENDING) finché la nuova non ha i suoi numeri: se no il logo, solo,
// scenderebbe in mezzo alla pillola per poi risalire.
export const PENDING = ""

export function usePhoneSummary(summary) {
	const { setSummary } = usePhoneTitle()
	// prima che lo schermo si ridisegni: cambiando pagina la pillola passa
	// dai numeri dell'una a quelli dell'altra senza un fotogramma di mezzo
	useLayoutEffect(() => {
		if (summary !== undefined) setSummary(summary || null)
	}, [summary, setSummary])
	useLayoutEffect(() => () => setSummary(PENDING), [setSummary])
}

// quanto stanno lontani titolo e pillola, e quanto può scendere il titolo
const TITLE_GAP = "0.75rem"
const MIN_FONT_PX = 22

// `side` è quanto sta già staccato dal bordo dello schermo chi contiene il
// titolo: mezzo rem negli allievi e nel quaderno (Container), niente nel
// profilo ("0px": dentro un calc lo zero vuole l'unità). La pillola sta a
// PHONE_ISLAND_SIDE dal bordo, e il titolo ne tiene conto per lasciarle il
// posto. `inset` lo stacca a sinistra, per farlo cadere sul bordo delle
// schede.
function PhonePageTitle({
	children,
	textSize = "display1",
	textWeight = "bold",
	side = "0.5rem",
	inset = "0",
}) {
	const { pillWidth } = usePhoneTitle()

	if (!isSmartphone) return null

	return (
		<div
			style={{
				paddingTop: PHONE_HEADER_TOP,
				paddingLeft: inset,
				paddingRight: `calc(${PHONE_ISLAND_SIDE} + ${pillWidth}px + ${TITLE_GAP} - ${side})`,
			}}>
			<div style={{ minHeight: PHONE_HEADER_HEIGHT, display: "flex", alignItems: "center" }}>
				{children && (
					<FittingTitle textSize={textSize} textWeight={textWeight} room={pillWidth}>
						{children}
					</FittingTitle>
				)}
			</div>
		</div>
	)
}

function FittingTitle({ children, textSize, textWeight, room }) {
	const ref = useRef(null)

	useLayoutEffect(() => {
		const el = ref.current
		if (!el) return
		const fit = () => {
			el.style.fontSize = ""
			if (el.scrollWidth <= el.clientWidth) return
			const full = parseFloat(window.getComputedStyle(el).fontSize)
			const fitted = Math.max(MIN_FONT_PX, Math.floor((full * el.clientWidth) / el.scrollWidth))
			el.style.fontSize = `${fitted}px`
		}
		fit()
		// la larghezza cambia girando il telefono; la scritta quando arriva
		// il carattere vero
		window.addEventListener("resize", fit)
		document.fonts?.ready.then(fit)
		return () => window.removeEventListener("resize", fit)
	}, [children, room])

	return (
		<Text
			ref={ref}
			tag="h1"
			textSize={textSize}
			textWeight={textWeight}
			textColor={theme.colors.gunmetal}
			m="0"
			style={{
				flex: 1,
				minWidth: 0,
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}>
			{children}
		</Text>
	)
}

export default PhonePageTitle
